const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const DRIVER_FEES_EXPORT_COLUMNS = [
  { key: "driverName", label: "Driver" },
  { key: "driverContact", label: "Driver Contact" },
  { key: "grNo", label: "GR No" },
  { key: "studentName", label: "Student Name" },
  { key: "fatherName", label: "Father Name" },
  { key: "classSection", label: "Class / Sec" },
  { key: "vehicleNo", label: "Vehicle" },
  { key: "customFee", label: "Custom Fee" },
  { key: "feeType", label: "Fee Type" },
  { key: "period", label: "Period" },
  { key: "netFee", label: "Net Fee" },
  { key: "received", label: "Received" },
  { key: "collectedInPeriod", label: "Collected (Period)" },
  { key: "balance", label: "Balance" },
  { key: "status", label: "Status" },
  { key: "receivedOn", label: "Received On" },
];

export function driverFeePeriodLabel(record) {
  if (!record) return "";
  if (record.monthYear) return record.monthYear;

  const month = Number(record.month);
  const year = Number(record.year);
  if (!month && !year) return "";

  const monthName = month >= 1 && month <= 12 ? MONTH_NAMES[month - 1] : "";
  return [monthName, year || ""].filter(Boolean).join(" ");
}

export function driverFeeStatusLabel(value) {
  const status = String(value || "").trim();
  if (!status) return "";
  return status
    .replace(/-/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function driverFeeDateLabel(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function classSectionLabel(student) {
  const cls = String(student?.className || "").trim();
  const sec = String(student?.sectionName || "").trim();
  if (cls && sec) return `${cls} / ${sec}`;
  return cls || sec || "";
}

function totalValue(totals, camelKey, summaryKey) {
  const value = totals?.[camelKey] ?? totals?.[summaryKey];
  return value === null || value === undefined ? "" : value;
}

/**
 * Flatten the LoadDriverFeesReport response into rows for the Excel export.
 * Includes a driver-total row after each driver and a grand-total row at the end.
 */
export function buildDriverFeesExportRows(driverBlocks, totals = {}) {
  const blocks = Array.isArray(driverBlocks) ? driverBlocks : [];
  const rows = [];

  blocks.forEach((driver) => {
    const driverName = driver?.name || "";
    const driverContact = driver?.contact || "";
    const students = driver?.students || [];

    students.forEach((student) => {
      const base = {
        driverName,
        driverContact,
        grNo: student?.grNo || "",
        studentName: student?.name || "",
        fatherName: student?.father_name || "",
        classSection: classSectionLabel(student),
        vehicleNo: student?.vehicleNo || "",
        customFee: student?.customFee ?? "",
      };
      const feeRecords = student?.feeRecords || [];

      if (!feeRecords.length) {
        rows.push({
          ...base,
          feeType: "",
          period: "",
          netFee: "",
          received: "",
          collectedInPeriod: "",
          balance: "",
          status: "",
          receivedOn: "",
        });
        return;
      }

      feeRecords.forEach((record) => {
        rows.push({
          ...base,
          feeType: record?.feeType || "",
          period: driverFeePeriodLabel(record),
          netFee: record?.netFee ?? "",
          received: record?.rcvd ?? "",
          collectedInPeriod: record?.collectedInPeriod ?? "",
          balance: record?.balance ?? "",
          status: driverFeeStatusLabel(record?.status),
          receivedOn: driverFeeDateLabel(record?.rcvdDate),
        });
      });
    });

    if (students.length) {
      rows.push({
        driverName,
        driverContact,
        grNo: "",
        studentName: "Driver total",
        fatherName: "",
        classSection: "",
        vehicleNo: "",
        customFee: "",
        feeType: "",
        period: "",
        netFee: totalValue(driver, "totalBilled", "billed"),
        received: totalValue(driver, "totalReceived", "received"),
        collectedInPeriod: totalValue(driver, "totalCollected", "collected"),
        balance: totalValue(driver, "totalOutstanding", "outstanding"),
        status: "",
        receivedOn: "",
      });
    }
  });

  if (rows.length) {
    rows.push({
      driverName: "",
      driverContact: "",
      grNo: "",
      studentName: "Grand total",
      fatherName: "",
      classSection: "",
      vehicleNo: "",
      customFee: "",
      feeType: "",
      period: "",
      netFee: totalValue(totals, "totalBilled", "billed"),
      received: totalValue(totals, "totalReceived", "received"),
      collectedInPeriod: totalValue(totals, "totalCollected", "collected"),
      balance: totalValue(totals, "totalOutstanding", "outstanding"),
      status: "",
      receivedOn: "",
    });
  }

  return rows;
}
